import { useState, useEffect, useRef } from 'react';
import { Button, Input, Space, Typography, Tooltip, Spin, Tag, Image } from 'antd';
import {
  UpOutlined, DownOutlined, DeleteOutlined,
  PictureOutlined, PaperClipOutlined, PlusOutlined,
} from '@ant-design/icons';
import { api } from '../api';

const { Text } = Typography;

const ACCEPT_IMAGES = 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml';
const ACCEPT_DOWNLOADS = 'application/pdf,application/zip,.pdf,.zip,.doc,.docx,.xls,.xlsx';

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      // e.target.result is "data:<mime>;base64,<data>" — strip the prefix
      const base64 = e.target.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function MediaItem({ item, index, total, onMoveUp, onMoveDown, onDelete, onDescriptionChange }) {
  const isImage = item.type === 'image';
  const dataUrl = `data:${item.mime_type};base64,${item.data}`;

  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'flex-start',
      padding: '10px 12px', background: '#fafafa',
      border: '1px solid #e8e8e8', borderRadius: 8, marginBottom: 8,
    }}>
      {/* Thumbnail / icon */}
      <div style={{ flexShrink: 0, width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isImage ? (
          <Image
            src={dataUrl}
            width={64} height={64}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            preview={{ src: dataUrl }}
          />
        ) : (
          <div style={{
            width: 64, height: 64, background: '#e6f4ff', borderRadius: 4,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}>
            <PaperClipOutlined style={{ fontSize: 22, color: '#1677ff' }} />
            <Text style={{ fontSize: 10, color: '#1677ff', textAlign: 'center', wordBreak: 'break-all', padding: '0 4px' }}>
              {item.filename.split('.').pop().toUpperCase()}
            </Text>
          </div>
        )}
      </div>

      {/* Description + filename */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}
          ellipsis={{ tooltip: item.filename }}>
          {item.filename}
        </Text>
        <Input
          size="small"
          placeholder="Kurze Beschreibung (optional)"
          value={item.description || ''}
          onChange={e => onDescriptionChange(item.id, e.target.value)}
        />
        <Tag color={isImage ? 'green' : 'blue'} style={{ marginTop: 4, fontSize: 11 }}>
          {isImage ? 'Bild' : 'Download'}
        </Tag>
      </div>

      {/* Controls */}
      <Space direction="vertical" size={4}>
        <Tooltip title="Nach oben">
          <Button size="small" icon={<UpOutlined />} disabled={index === 0} onClick={() => onMoveUp(index)} />
        </Tooltip>
        <Tooltip title="Nach unten">
          <Button size="small" icon={<DownOutlined />} disabled={index === total - 1} onClick={() => onMoveDown(index)} />
        </Tooltip>
        <Tooltip title="Löschen">
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(item.id)} />
        </Tooltip>
      </Space>
    </div>
  );
}

export default function MediaEditor({ productId, entityId, apiMedia, placeholder }) {
  // Support both old (productId + api.media) and new (entityId + apiMedia) prop style
  const resolvedId = entityId ?? productId;
  const resolvedApi = apiMedia ?? api.media;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef();
  const downloadInputRef = useRef();

  useEffect(() => {
    if (!resolvedId) return;
    setLoading(true);
    resolvedApi.list(resolvedId)
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [resolvedId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleFileSelect(e, type) {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;

    setUploading(true);
    try {
      for (const file of files) {
        const base64 = await readFileAsBase64(file);
        const created = await resolvedApi.create(resolvedId, {
          type,
          filename: file.name,
          mime_type: file.type || 'application/octet-stream',
          description: null,
          data: base64,
        });
        setItems(prev => [...prev, created]);
      }
    } finally {
      setUploading(false);
    }
  }

  async function handleMoveUp(index) {
    const next = [...items];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    setItems(next);
    await resolvedApi.reorder(resolvedId, next.map(i => i.id));
  }

  async function handleMoveDown(index) {
    const next = [...items];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    setItems(next);
    await resolvedApi.reorder(resolvedId, next.map(i => i.id));
  }

  async function handleDelete(id) {
    await resolvedApi.delete(id);
    setItems(prev => prev.filter(i => i.id !== id));
  }

  async function handleDescriptionChange(id, value) {
    setItems(prev => prev.map(i => i.id === id ? { ...i, description: value } : i));
    await resolvedApi.update(id, { description: value });
  }

  if (!resolvedId) {
    return <Text type="secondary">{placeholder || 'Bitte zuerst speichern.'}</Text>;
  }

  return (
    <Spin spinning={loading || uploading}>
      <Space style={{ marginBottom: 12 }}>
        <input
          ref={imageInputRef} type="file" accept={ACCEPT_IMAGES} multiple hidden
          onChange={e => handleFileSelect(e, 'image')}
        />
        <input
          ref={downloadInputRef} type="file" accept={ACCEPT_DOWNLOADS} multiple hidden
          onChange={e => handleFileSelect(e, 'download')}
        />
        <Button icon={<PictureOutlined />} onClick={() => imageInputRef.current.click()}>
          Bild hinzufügen
        </Button>
        <Button icon={<PlusOutlined />} onClick={() => downloadInputRef.current.click()}>
          Download hinzufügen
        </Button>
      </Space>

      {items.length === 0 && !loading && (
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Noch keine Medien vorhanden.
        </Text>
      )}

      {items.map((item, index) => (
        <MediaItem
          key={item.id}
          item={item}
          index={index}
          total={items.length}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          onDelete={handleDelete}
          onDescriptionChange={handleDescriptionChange}
        />
      ))}
    </Spin>
  );
}
